import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, TrackedVisitorDTO, VisitorTrackerDTO } from '../../../../services/api/api';
import { UtilService } from '../../../../services/util.service';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { NavigationService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-comp-tracker-details-tracked-visitors',
  templateUrl: './tracker-details-tracked-visitors.component.html',
  styleUrls: ['./tracker-details-tracked-visitors.component.scss'],
  standalone: false,
})
export class TrackerDetailsTrackedVisitorsComponent implements OnInit {
  trackedVisitors: TrackedVisitorDTO[];
  updatedTrackedVisitors: TrackedVisitorDTO[];
  tracker: VisitorTrackerDTO;
  loading: boolean;

  from: SbDateTime;
  till: SbDateTime;

  hourTimeSlot: string[];
  dateRange: SbDateTime[];

  editMode: number;

  constructor(
    public util: UtilService,
    private api: API,
    private activatedRoute: ActivatedRoute,
    public errorHandler: ErrorService,
    private modal: ModalService,
    private navService: NavigationService,
    public translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.trackedVisitors = [];
    this.updatedTrackedVisitors = [];
    this.tracker = undefined;
    this.loading = true;
    this.from = new SbDateTime(DateTime.local().minus({ day: 8 }));
    this.till = new SbDateTime(DateTime.local().minus({ day: 1 }));
    this.hourTimeSlot = Array.from(new Array(24), (x, i) => this.util.getHourTimeSlotString(i));
    this.dateRange = [];
    this.editMode = 0;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params?.trackerID) {
        this.api
          .dm_visitor_VisitorTracker_retrieveById(+params.trackerID)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (tracker: VisitorTrackerDTO) => {
              this.tracker = tracker;
              this.refresh();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    });
  }

  getDaysArray(startDate: SbDateTime, endDate: SbDateTime, includeEndDate: boolean): SbDateTime[] {
    const dates: SbDateTime[] = [];
    let currentDate: SbDateTime = startDate;
    while (currentDate.value < endDate.value) {
      dates.push(currentDate);
      currentDate = new SbDateTime(currentDate.value.plus({ day: 1 }));
    }
    if (includeEndDate) {
      dates.push(endDate);
    }
    return dates;
  }

  refresh() {
    this.loading = true;
    this.dateRange = this.getDaysArray(this.from, this.till, true);
    this.api
      .dm_visitor_VisitorTracker_retrieveTrackedVisitorsByTrackerId(this.tracker.id, this.from, this.till)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (receivedTrackedVisitor: TrackedVisitorDTO[]) => {
          if (receivedTrackedVisitor?.length > 0) {
            this.trackedVisitors = receivedTrackedVisitor;
          }
        },
        error: error => {
          if (error.status && (+error.status === 403 || +error.status === 404)) {
            this.navService.navigateToPoint(this.util.RoutingID.tracker);
          } else {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (dates?.fromDate && dates?.toDate) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.refresh();
    }
  }

  getTrackedVisitor(date: SbDateTime): TrackedVisitorDTO {
    for (const trackedVisitor of this.trackedVisitors) {
      if (
        date.value.year === trackedVisitor.date.value.year &&
        date.value.month === trackedVisitor.date.value.month &&
        date.value.day === trackedVisitor.date.value.day
      ) {
        return trackedVisitor;
      }
    }
    return undefined;
  }

  edit() {
    if (this.editMode === 1) {
      this.editMode = 0;
      if (this.updatedTrackedVisitors.length > 0) {
        this.api
          .dm_visitor_VisitorTracker_createTrackedVisitorsAtTracker(this.tracker.id, this.updatedTrackedVisitors)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (receivedTrackedVisitors: TrackedVisitorDTO[]) => {
              this.trackedVisitors = receivedTrackedVisitors;
              this.updatedTrackedVisitors = [];
              this.refresh();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.editMode = 1;
    }
  }

  cancel() {
    this.editMode = -1;
    this.updatedTrackedVisitors = [];
    this.refresh();
  }

  handleTrackerValueChanged(trackedVisitor: TrackedVisitorDTO) {
    const index = this.getIndexOfTrackedVisitor(trackedVisitor);
    if (index === -1) {
      this.updatedTrackedVisitors.push(trackedVisitor);
    } else {
      this.updatedTrackedVisitors[index] = trackedVisitor;
    }
  }

  getIndexOfTrackedVisitor(trackedVisitor): number {
    if (this.updatedTrackedVisitors.length > 0) {
      for (const [i, tVisitors] of this.updatedTrackedVisitors.entries()) {
        if (
          tVisitors.date.value.year === trackedVisitor.date.value.year &&
          tVisitors.date.value.month === trackedVisitor.date.value.month &&
          tVisitors.date.value.day === trackedVisitor.date.value.day
        ) {
          return i;
        }
      }
    }
    return -1;
  }
}
